/**
 * Uppercases the first letter of every word in a phrase. Already-capitalized
 * letters and every character after the first of each word are left untouched,
 * so acronyms and intentional casing (e.g. "FSE", "WO", "SAP") are preserved.
 *
 * Examples:
 *   capWords("start date")        -> "Start Date"
 *   capWords("FSE Name")          -> "FSE Name"
 *   capWords("WO due date")       -> "WO Due Date"
 *   capWords("quantity of assets") -> "Quantity Of Assets"
 */
export const capWords = (value: string): string =>
    value ? value.replace(/\b([a-z])/g, m => m.toUpperCase()) : value;

// SAP country codes whose facilities display NAME + NAME 2 joined (MMG-4192).
export const concatenatedNameCountryCodes = ['DE', 'AT'];

/**
 * Display-only facility name; never mutates the stored record. Joins NAME and
 * NAME 2 for countries in concatenatedNameCountryCodes, else (or when NAME 2 is
 * blank) returns NAME alone with no trailing space or separator.
 */
export const getFacilityDisplayName = (
    name: string | undefined,
    name2: string | undefined,
    sapCountryCode: string | undefined
): string => {
    const baseName = name ?? '';
    const second = name2 ? name2.trim() : '';
    const code = sapCountryCode ? sapCountryCode.toUpperCase() : '';
    return second.length > 0 && concatenatedNameCountryCodes.includes(code)
        ? `${baseName} ${second}`.trim()
        : baseName;
};

// Countries that store the street number in Address 2 and expect it rendered
// *before* the street name (Address) — e.g. "123 Main St". Every other country
// keeps the stored order (Address then Address 2).
// Mirrors constants.usCountryID / constants.canadaCountryID — inlined so this
// widely-imported helper stays free of the heavy constants module (boots MSAL).
const US_COUNTRY_ID = 'abc5d95c-129f-4837-988c-0bf4ae1f3b67';
const CA_COUNTRY_ID = '2fcbfdc4-9c25-4b58-874c-7c2625eaa750';
const streetNumberFirstCountryIDs: string[] = [US_COUNTRY_ID, CA_COUNTRY_ID];

export const isStreetNumberFirstCountry = (
    countryID: string | undefined
): boolean => !!countryID && streetNumberFirstCountryIDs.includes(countryID);

/**
 * Returns the two street lines (Address / Address 2) ordered for display.
 * For US & Canada the street number (Address 2) comes before the street name
 * (Address); all other countries keep the stored order. Empty parts are removed
 * so callers can render or join whatever remains.
 */
export const orderStreetLines = (
    address: string | undefined,
    address2: string | undefined,
    countryID: string | undefined
): string[] => {
    const streetName = (address ?? '').trim();
    const streetNumber = (address2 ?? '').trim();
    const ordered = isStreetNumberFirstCountry(countryID)
        ? [streetNumber, streetName]
        : [streetName, streetNumber];
    return ordered.filter(Boolean);
};

/** Street lines joined into a single space-separated segment. */
export const formatFacilityStreet = (
    address: string | undefined,
    address2: string | undefined,
    countryID: string | undefined
): string => orderStreetLines(address, address2, countryID).join(' ');
